import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import { colors } from '../../colors';

const InstanceOverview = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        const response = await axiosInstance.get('/api/surveys');
  
        // Sort by validTill ascending (earliest first)
        const sorted = response.data.sort((a, b) => {
          const dateA = new Date(a.validTill);
          const dateB = new Date(b.validTill);
          return dateA - dateB;
        });
  
        setSurveys(sorted);
      } catch (err) {
        console.error('Error fetching survey data:', err);
        setError('Failed to load surveys.');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
  
    fetchSurveys();
  }, []);
  

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleSurveyClick = (surveyId) => {
    navigate(`/admin/survey/${surveyId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 4 }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          color: colors.racineRedDark,
          fontWeight: 'bold',
          textAlign: 'center',
          mb: 3,
        }}
      >
        Instances
      </Typography>

      {/* Header Row */}
      {surveys.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 'bold',
            mb: 1,
            px: 2,
            color: colors.primaryApplicationText,
          }}
        >
          <Box sx={{ display: 'flex', width: '90%' }}>
            <Typography sx={{ width: '25%' }}>Survey Name</Typography>
            <Typography sx={{ width: '25%' }}>University</Typography>
            <Typography sx={{ width: '25%' }}>Created</Typography>
            <Typography sx={{ width: '25%' }}>Valid Till</Typography>
          </Box>

          <Box sx={{ width: '10%', textAlign: 'right' }}>
            <Typography>Links</Typography>
          </Box>
        </Box>
      )}

      {/* Survey List */}
      {surveys.map((survey) => (
        <Card
          key={survey.id}
          sx={{
            mb: 1.5,
            px: 2,
            py: 1,
            cursor: 'pointer',
            borderLeft: `4px solid ${colors.primaryBannerBlue}`,
            '&:hover': {
              backgroundColor: '#f9f9f9',
            },
          }}
          onClick={() => handleSurveyClick(survey.id)}
        >
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', width: '90%' }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color={colors.primaryApplicationText}
                  sx={{ width: '25%' }}
                >
                  {survey.templateSnapshot?.title || survey.surveyName}
                </Typography>

                <Typography variant="body2" sx={{ color: '#555', width: '25%' }}>
                  {survey.universityName}
                </Typography>

                <Typography variant="body2" sx={{ color: '#999', width: '25%' }}>
                  {formatDate(survey.createdDate)}
                </Typography>

                <Typography variant="body2" sx={{ color: '#999', width: '25%' }}>
                  {formatDate(survey.validTill)}
                </Typography>
              </Box>

              <Box sx={{ width: '10%', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#999' }}>
                  {survey.uniqueLinksCount}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={error}
      />
    </Box>
  );
};

export default InstanceOverview;
